import {MultiSelect} from './models/multi-select';
import {MultiSelectItem} from './interfaces/multi-select-item';
import { pdsI18n } from './pds-i18n';
import { PolymerKeyboardEvent } from './interfaces/polymer-keyboard-event';
import { PolymerMouseEvent } from './interfaces/polymer-mouse-event';
import { SinglePopoverWidget } from './single-popover-widget';
import { pdsLogger } from './pds-logger';
import { pdsUtils } from './pds-utils';
import { PopperPlacement } from './popper-configuration';
import {PopoverEngine} from './interfaces/popover-engine';
import {ValidationResult} from 'common-utils/interfaces';
import {PdsAbstractInputWidget} from './pds-abstract-input-widget';
import {pdsValidation} from './pds-validation';
let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('input-widgets'), 'pds-multi-select-widget.ts');

interface MultiSelectModel {
  selected: MultiSelectItem;
}

export class PdsMultiSelectWidget extends SinglePopoverWidget {
  is = 'pds-multi-select-widget';

  //Input
  id: string;

  //Input
  multiSelect: MultiSelect;

  //Input
  dataButtonText = 'Select';

  //Input
  dataButtonAriaText: string | undefined;

  dataShowLabel: string;
  dataLabelText: string;
  isBadgeHidden = true;
  isBadgeElevated = false;
  dataTooltipText: string;
  isAttached = false;
  isValid = true;
  dataBadgeText: string;
  dataDisableInvalidBadgeElevation: string;
  invalidRule: string | undefined;
  dataIsrequired: string;
  dataHideUnelevatedBadge: string;
  originalBadgeText: string;
  errorMessage: string;
  dataCustomvalidate: string;

  // this variable is initially set to false and corresponds to the on-blur property of an input field
  // on-blur is tied to a function that (on-blur="functionName") fires a FocusEvent.
  // inside that function, we set this value to true only if there are any validation errors.
  errorPresentWhenBlurOccurred = false;

  //right now the only things that make that happen are focusLost and pds.validation.validateElements
  showValidIndicator: boolean; // is false until field has focusLost.

  // each input widget must have a reference to its own error field, so that we can
  // add the hidden class to dynamically show and hide the error.
  myErrorField: HTMLElement;

  disableErrorMessageLabelText: string | undefined;

  showFloatingPanel = false;

  properties = {
    id: {
      type: String,
      value: null
    },
    multiSelect: {
      observer: 'updateMultiSelect',
      value: null
    },
    dataButtonText: {
      observer: 'updateButtonText',
      type: String,
      value: 'Select'
    },
    dataButtonAriaText: {
      type: String,
      value: null
    },
    dataShowLabel: {
      type: String,
      value: null
    },
    dataLabelText: {
      type: String,
      value: null
    },
    dataTooltipText: {
      type: String,
      value: null
    },
    dataHelperText: {
      type: String,
      value: null
    },
    dataBadgeText: {
      type: String,
      value: '',
      observer: 'setUpBadge'
    },
    dataDisableInvalidBadgeElevation: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataIsrequired: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataHideUnelevatedBadge: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataCustomvalidate: {
      type: String,
      value: null
    },
    disableErrorMessageLabelText: {
      type: String,
      value: null
    },
    dataIsFieldOnly: {
      type: String,
      value: 'false'
    }
  };

  /**
   * TypeScript does not officially support multiple inheritance so we need to use some different syntax to inherit
   * from PdsAbstractInputWidget in addition to the SiglePopoverWidget that this class already extends.
   * Below are the definitions of the functions that we need access to from the PdsAbstractInputWidget.
   * At the bottom of this file we copy over the actual functions from prototype to prototype.
   */
  setUpBadge: () => void;
  showErrorMessage: (makeVisible: boolean) => void;
  setError: (errorMsg: string) => void;
  showLabel: (dataShowLabel: string, dataLabelText: string) => boolean;
  showFieldHelp: (dataFieldHelpText: string) => boolean;
  validateWidget: (showValidationResult?: boolean, fromBlur?: boolean) => boolean;
  /**
   * End of PdsAbstractInputFunctionDefinitions
   */

  private isReady = false;

  attached(): void {
    super.attached();
    this.isAttached = true;
    this.setUpBadge();
  }

  ready(): void {
    this.showFloatingPanel = false;
    this.isReady = true;
    PdsAbstractInputWidget.prototype.ready.call(this);
    if (this.multiSelect) {
      this.watchMultiSelect();
    }
  }

  watchMultiSelect(): void {
    this.watch('multiSelect.multiSelectData');
    this.multiSelect.selectionChange.subscribe(() => {
      this.validateWidget();
    });
  }

  keyboardChange(event: PolymerKeyboardEvent<MultiSelectModel>): void {
    if (event.key === 'Enter') {
      this.inputChange(event);
    }
  }

  inputChange(event: PolymerKeyboardEvent<MultiSelectModel> | PolymerMouseEvent<MultiSelectModel>): void {
    if (event.target && event.target instanceof HTMLInputElement)  {
      //event.model is the template with the dom-repeat.  selected is the named item (MultiSelectItem) from the loop.
      event.model.selected.isSelected = event.target.checked;

      let labelEl: HTMLElement | undefined = <HTMLElement | undefined>pdsUtils.getClosest(event.target, 'label');
      if (labelEl != null) {
        labelEl.classList.toggle('pds-is-selected', event.target.checked);
      }
      this.validateWidget();
      this.multiSelect.selectionChange.next(event.model.selected);
    }
  }

  updateMultiSelect(): void {
    log('updateMultiSelect() called');
    if (this.isReady && this.multiSelect) {
      log('watching multiSelectData');
      this.watchMultiSelect();
    }
  }

  updateButtonText(): void {
    if (this.dataButtonText == null) {
      this.dataButtonText = pdsI18n.getText('pds|multi select|Select');
    }
  }

  isRequiredDataDefined(): boolean {
    return this.multiSelect != null;
  }

  getPopoverSelector(): string {
    return '#popover-' + this.id;
  }

  getPopoverTriggerSelector(): string {
    return '.pds-panel-floating-trigger';
  }

  getDefaultAlignment(): PopperPlacement {
    return 'bottom-start';
  }

  log(message?: any, ...optionalParams: any[]): void {
    log(message, ...optionalParams);
  }

  onPopoverHidden(popoverEngineInstance: PopoverEngine): void {
    super.onPopoverHidden(popoverEngineInstance);
    this.validateWidget(true, true);
    this.showFloatingPanel = false;
  }

  onPopoverShown(popoverEngineInstance: PopoverEngine): void {
    this.forceUpdateCheckBoxStates();
    super.onPopoverShown(popoverEngineInstance);
    this.showFloatingPanel = true;
  }

  /**
   * There are some states the widget can get into where Polymer won't draw the DOM correctly.
   * Calling this function updates the states of the check boxes through the DOM manually.
   */
  forceUpdateCheckBoxStates(): void {
    for (let labelElement of Array.from(this.popoverContentElement.querySelectorAll('label'))) {
      let multiSelectItem: MultiSelectItem | undefined = (<any>labelElement).multiSelectItem;
      if (multiSelectItem != null) {
        labelElement.classList.toggle('pds-is-selected', multiSelectItem.isSelected === true);
        let checkboxInput = labelElement.querySelector('input');
        if (checkboxInput != null) {
          checkboxInput.checked = multiSelectItem.isSelected === true;
        }
      }
    }
  }

  runValidationEngine(): ValidationResult {
    let result: ValidationResult = {isValid: true};

    if (this.dataIsrequired === 'true') {
      let item: MultiSelectItem | undefined = this.multiSelect.multiSelectData.find((item: MultiSelectItem) => {
        return item.isSelected === true;
      });
      /**
       * Item is defined - User checked something
       * Item is not defined - User did not check anything
       */
      if (item == null) {
        result.isValid = false;
        result.invalidRule = 'isRequired';
        return result;
      }
    }

    if (this.dataCustomvalidate != null) {
      let validationFunction = pdsValidation.getCustomValidate(this.dataCustomvalidate);
      if (validationFunction == null) {
        console.error('Custom validation function was not found');
      } else {
        return validationFunction('', {element: this, multiSelect: this.multiSelect}, {});
      }
    }
    return result;
  }

  generateValidationRules(): void {
    //No-Op.  This is here so we can use the ready() function from PdsAbstractInputWidget.
    //Do Not Delete.
  }
}

//TypeScript doesn't support multipe inheritance... unless you know JavaScript really well.  :)
PdsMultiSelectWidget.prototype.setUpBadge = PdsAbstractInputWidget.prototype.setUpBadge;
PdsMultiSelectWidget.prototype.validateWidget = PdsAbstractInputWidget.prototype.validateWidget;
PdsMultiSelectWidget.prototype.showFieldHelp = PdsAbstractInputWidget.prototype.showFieldHelp;
PdsMultiSelectWidget.prototype.showLabel = PdsAbstractInputWidget.prototype.showLabel;
PdsMultiSelectWidget.prototype.setError = PdsAbstractInputWidget.prototype.setError;
PdsMultiSelectWidget.prototype.showErrorMessage = PdsAbstractInputWidget.prototype.showErrorMessage;
